import React, { useRef, useState, useEffect } from "react"
import { makeStyles } from "@material-ui/core"
import { Container, Typography, IconButton } from "@material-ui/core/index"
import { injectIntl, Link } from "gatsby-plugin-intl"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import ChevronRightIcon from "@material-ui/icons/ChevronRight"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import lm34 from "../images/before-after/LM-34.webp"
import ex1 from "../images/signature-examples-3/ex1-signature.webp"
import ex2 from "../images/signature-examples-3/ex2-signature.webp"
import ex3 from "../images/signature-examples-3/ex3-signature.webp"
import ex4 from "../images/signature-examples-3/ex4-signature.webp"
import ex5 from "../images/signature-examples-3/ex5-signature.webp"
import ex6 from "../images/signature-examples-3/ex6-signature.webp"
import { ACCENT } from "../styles/cta"

const useStyles = makeStyles((theme) => ({
  sectionContainer: {
    backgroundColor: "#1a1a1a",
    padding: theme.spacing(10, 0),
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(7, 0),
    },
  },
  sectionHeader: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: theme.spacing(0, 4),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 2.5),
    },
  },
  sectionHeaderText: {
    color: "#fff",
    fontWeight: 300,
    fontSize: "2.3rem",
    lineHeight: 1.15,
    ["@media (max-width:600px)"]: {
      fontSize: "1.9rem",
    },
  },
  scrollWrapper: {
    position: "relative",
    width: "100%",
  },
  scrollContainer: {
    display: "flex",
    overflowX: "auto",
    gap: "20px",
    padding: "0 40px",
    scrollBehavior: "smooth",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    scrollbarWidth: "none",
    ["@media (max-width:600px)"]: {
      padding: "0 20px",
      gap: "12px",
      margin: "0 -16px",
      scrollSnapType: "x mandatory",
      scrollPaddingLeft: "20px",
    },
  },
  scrollButtonRight: {
    position: "absolute",
    right: "20px",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "transparent",
    color: "#cfcccf",
    zIndex: 2,
    padding: "8px",
    transition: "opacity 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
    ["@media (max-width:600px)"]: {
      right: "-10px",
    },
  },
  scrollButtonLeft: {
    position: "absolute",
    left: "20px",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "transparent",
    color: "#cfcccf",
    zIndex: 2,
    padding: "8px",
    transition: "opacity 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
    ["@media (max-width:600px)"]: {
      left: "-10px",
    },
  },
  scrollIcon: {
    fontSize: "48px",
    height: "60px",
    width: "auto",
  },
  exampleImage: {
    width: "auto",
    height: "450px",
    maxWidth: "100%",
    objectFit: "cover",
    borderRadius: "8px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
    ["@media (max-width:600px)"]: {
      flex: "none",
      width: "84vw",
      height: "auto",
      scrollSnapAlign: "start",
    },
  },
  seeMoreRow: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: theme.spacing(0, 4),
    marginTop: theme.spacing(4),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0, 2.5),
    },
  },
  seeMoreLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    color: ACCENT,
    textDecoration: "none",
    fontSize: "0.95rem",
    fontWeight: 500,
    borderBottom: `1px solid ${ACCENT}`,
    paddingBottom: 2,
    transition: "opacity 0.2s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },
  arrow: {
    fontSize: "18px",
  },
}))

const SignatureExamples = ({ intl }) => {
  const classes = useStyles()
  const scrollRef = useRef(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)
  const [imagesLoaded, setImagesLoaded] = useState(0)

  const examples = [lm34, ex1, ex2, ex3, ex4, ex5, ex6].map((src, i) => ({
    src,
    alt: intl.formatMessage({ id: "home.signatureExamples.alt" }, { n: i + 1 }),
  }))

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
      const isAtStart = scrollLeft <= 10
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 5

      setShowLeftArrow(!isAtStart)
      setShowRightArrow(!isAtEnd)
    }
  }

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition)
      checkScrollPosition()
      return () =>
        scrollElement.removeEventListener("scroll", checkScrollPosition)
    }
  }, [])

  // Re-check scroll position when all images are loaded
  useEffect(() => {
    if (imagesLoaded === examples.length) {
      checkScrollPosition()
    }
  }, [imagesLoaded])

  // One photo plus the gap between photos, so each arrow press moves one result.
  const scrollByOne = (direction) => {
    const el = scrollRef.current
    if (!el || !el.firstElementChild) return
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0
    const step = el.firstElementChild.getBoundingClientRect().width + gap
    el.scrollBy({ left: direction * step, behavior: "smooth" })
    setTimeout(checkScrollPosition, 400)
  }

  const handleScrollRight = () => scrollByOne(1)
  const handleScrollLeft = () => scrollByOne(-1)

  return (
    <section className={classes.sectionContainer}>
      <div className={classes.sectionHeader}>
        <Typography component="h2" className={classes.sectionHeaderText}>
          {intl.formatMessage({ id: "home.signatureExamples.title" })}
        </Typography>
      </div>
      <Container maxWidth="xl">
        <div className={classes.scrollWrapper}>
          <div className={classes.scrollContainer} ref={scrollRef}>
            {examples.map((example, index) => (
              <img
                key={index}
                src={example.src}
                alt={example.alt}
                width={1080}
                height={1350}
                // only the first two can be (partially) visible in the scroller;
                // the rest load lazily as the user scrolls toward them
                loading={index < 2 ? "eager" : "lazy"}
                decoding="async"
                className={classes.exampleImage}
                onLoad={() => setImagesLoaded((prev) => prev + 1)}
              />
            ))}
          </div>
          {showLeftArrow && (
            <IconButton
              className={classes.scrollButtonLeft}
              onClick={handleScrollLeft}
              aria-label={intl.formatMessage({
                id: "home.signatureExamples.scrollLeft",
              })}
            >
              <ChevronLeftIcon className={classes.scrollIcon} />
            </IconButton>
          )}
          {showRightArrow && (
            <IconButton
              className={classes.scrollButtonRight}
              onClick={handleScrollRight}
              aria-label={intl.formatMessage({
                id: "home.signatureExamples.scrollRight",
              })}
            >
              <ChevronRightIcon className={classes.scrollIcon} />
            </IconButton>
          )}
        </div>
      </Container>
      <div className={classes.seeMoreRow}>
        <Link to="/deep-plane-facelift/" className={classes.seeMoreLink}>
          {intl.formatMessage({ id: "home.signatureExamples.seeMore" })}
          <ArrowForwardIcon className={classes.arrow} />
        </Link>
      </div>
    </section>
  )
}

export default injectIntl(SignatureExamples)
